import { useState } from "react";
import {
  Chip,
  Collapse,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import type {
  CandidateGateOutcome,
  ResolvedDocumentCandidateTrace,
  ResolvedDocumentSelectionTrace,
  SourceJudgmentTrace,
  SourceVerdict,
} from "../../../../types/claims";
import { PaginationFooter } from "../../../primitives/data-display/PaginationFooter/PaginationFooter";
import tooltips from "./tooltips.json";
import {
  MonoCell,
  CollapsibleCell,
  CollapseInner,
} from "../VerificationTracePanel/trace_table_cells";

interface DocumentSelectionTracePanelProps {
  trace: ResolvedDocumentSelectionTrace;
  /** What the judge concluded about each source it read; a source absent here was not judged. */
  judgments?: SourceJudgmentTrace[];
}

const CANDIDATE_PAGE_SIZE_OPTIONS = [25, 50, 100];

const OUTCOME_LABEL: Record<CandidateGateOutcome, string> = {
  selected: "selected",
  reserve: "reserve",
  dropped_low_similarity: "low similarity",
  dropped_semantic_limit: "semantic limit",
  dropped_low_quality: "low quality",
  dropped_quality_limit: "quality limit",
};

const OUTCOME_TIP: Record<CandidateGateOutcome, string> = tooltips.outcomes;

function outcomeColor(outcome: CandidateGateOutcome): "success" | "warning" | "default" {
  if (outcome === "selected") return "success";
  if (outcome === "dropped_semantic_limit" || outcome === "dropped_quality_limit") {
    return "warning";
  }
  return "default";
}

function fmt(n: number, decimals = 3): string {
  return n.toFixed(decimals);
}

function isSelected(candidate: ResolvedDocumentCandidateTrace): boolean {
  return candidate.outcome === "selected";
}

/** Whether the judge read the candidate: the reserve source is judged like a selected one. */
function isJudged(candidate: ResolvedDocumentCandidateTrace): boolean {
  return candidate.outcome === "selected" || candidate.outcome === "reserve";
}

function judgeVerdictColor(verdict: SourceVerdict): "success" | "error" | "warning" | "default" {
  if (verdict === "SUPPORTS") return "success";
  if (verdict === "CONTRADICTS") return "error";
  if (verdict === "IRRELEVANT") return "warning";
  return "default";
}

function bestSimilarity(candidate: ResolvedDocumentCandidateTrace): number {
  return Math.max(...candidate.paragraphs.map((c) => c.semantic_similarity));
}

/** Judged documents first, then by document score (best paragraph similarity on traces that
 *  predate scores) within each group. */
function compareCandidates(
  a: ResolvedDocumentCandidateTrace,
  b: ResolvedDocumentCandidateTrace
): number {
  const bySelected = Number(isJudged(b)) - Number(isJudged(a));
  if (bySelected !== 0) return bySelected;
  const byScore = (b.document_score ?? 0) - (a.document_score ?? 0);
  if (byScore !== 0) return byScore;
  return bestSimilarity(b) - bestSimilarity(a);
}

const SmallOutcomeChip = styled(Chip)({ cursor: "help" });

const TruncatedSourceCell = styled(TableCell)({
  maxWidth: 320,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const DenseBreakdownTable = styled(Table)({
  "& .MuiTableCell-root": { fontSize: "0.72rem", paddingTop: 4, paddingBottom: 4 },
  "& .MuiTableCell-head": { fontSize: "0.68rem", fontWeight: 600 },
});

const ResultsPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  flex: "1 1 260px",
}));

const ConfigPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  flex: "1 1 340px",
}));

const ConfigStat = styled("div")({ textAlign: "center", minWidth: 70, cursor: "help" });

const TablePaper = styled(Paper)({ overflow: "auto" });

const TableIntro = styled("div")(({ theme }) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(2)} 0`,
}));

const DenseTable = styled(Table)(({ theme }) => ({
  minWidth: 600,
  marginTop: theme.spacing(1),
  "& .MuiTableCell-root": { fontSize: "0.72rem", paddingTop: 4, paddingBottom: 4 },
  "& .MuiTableCell-head": { fontSize: "0.68rem", fontWeight: 600 },
}));

function OutcomeChip({ outcome }: { outcome: CandidateGateOutcome }) {
  return (
    <Tooltip title={OUTCOME_TIP[outcome]} placement="top" arrow>
      <SmallOutcomeChip
        size="small"
        label={OUTCOME_LABEL[outcome]}
        color={outcomeColor(outcome)}
        variant="outlined"
      />
    </Tooltip>
  );
}

function ParagraphBreakdown({ candidate }: { candidate: ResolvedDocumentCandidateTrace }) {
  const chunkTooltips = new Map(tooltips.chunkColumns.map((c) => [c.key, c.tip]));
  return (
    <DenseBreakdownTable size="small">
      <TableHead>
        <TableRow>
          <TableCell>
            <Tooltip title={chunkTooltips.get("chunk_order")!} placement="top" arrow>
              <span style={{ cursor: "help" }}>Paragraph</span>
            </Tooltip>
          </TableCell>
          <TableCell align="right">
            <Tooltip title={chunkTooltips.get("semantic_similarity")!} placement="top" arrow>
              <span style={{ cursor: "help" }}>Similarity</span>
            </Tooltip>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[...candidate.paragraphs]
          .sort((a, b) => b.semantic_similarity - a.semantic_similarity)
          .map((p) => (
            <TableRow key={`${p.section_key}::${p.section_ordinal}::${p.paragraph_ordinal}`}>
              <TableCell>{`${p.section_key} §${p.section_ordinal}:${p.paragraph_ordinal}`}</TableCell>
              <MonoCell align="right">{fmt(p.semantic_similarity)}</MonoCell>
            </TableRow>
          ))}
      </TableBody>
    </DenseBreakdownTable>
  );
}

function JudgeVerdictCell({ verdict }: { verdict: SourceVerdict | undefined }) {
  return (
    <TableCell>
      {verdict ? (
        <Chip
          size="small"
          label={verdict.toLowerCase()}
          color={judgeVerdictColor(verdict)}
          variant="outlined"
        />
      ) : (
        "—"
      )}
    </TableCell>
  );
}

function CandidateRow({
  candidate,
  judgeVerdict,
}: {
  candidate: ResolvedDocumentCandidateTrace;
  judgeVerdict: SourceVerdict | undefined;
}) {
  const [open, setOpen] = useState(false);
  const label = candidate.title || candidate.source_id;

  return (
    <>
      <TableRow hover>
        <TruncatedSourceCell>
          <Link href={candidate.url} target="_blank" rel="noopener noreferrer" title={label}>
            <Typography variant="caption">{label}</Typography>
          </Link>
        </TruncatedSourceCell>
        <MonoCell align="right">
          {candidate.document_score !== null ? fmt(candidate.document_score) : "—"}
        </MonoCell>
        <MonoCell align="right">
          {candidate.cross_design_quality !== null ? fmt(candidate.cross_design_quality) : "—"}
        </MonoCell>
        <JudgeVerdictCell verdict={judgeVerdict} />
        <TableCell align="right">
          <Stack direction="row" gap={0.5} alignItems="center" justifyContent="flex-end">
            <OutcomeChip outcome={candidate.outcome} />
            <IconButton size="small" onClick={() => setOpen((v) => !v)}>
              {open ? (
                <KeyboardArrowUpIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <CollapsibleCell colSpan={5} $open={open}>
          <Collapse in={open} unmountOnExit>
            <CollapseInner>
              <ParagraphBreakdown candidate={candidate} />
            </CollapseInner>
          </Collapse>
        </CollapsibleCell>
      </TableRow>
    </>
  );
}

/** A single "label / value" stat tile with its own tooltip — used for the config gates so
 *  threshold and limit never look like a fraction of each other. */
function StatTile({
  configKey,
  value,
}: {
  configKey: (typeof tooltips.config)[number]["key"];
  value: string | number;
}) {
  const entry = tooltips.config.find((c) => c.key === configKey)!;
  return (
    <Tooltip title={entry.tip} placement="top" arrow>
      <ConfigStat>
        <Typography variant="body1" fontFamily="monospace">
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {entry.label}
        </Typography>
      </ConfigStat>
    </Tooltip>
  );
}

/** Trace panel for the vector-search + reranking stage that selects candidate documents. */
export function DocumentSelectionTracePanel({
  trace,
  judgments,
}: DocumentSelectionTracePanelProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(CANDIDATE_PAGE_SIZE_OPTIONS[0]);
  const columnTooltips = new Map(tooltips.columns.map((c) => [c.key, c.tip]));

  const documentCount = trace.candidates.length;
  const matchingDocumentCount = trace.candidates.filter(
    (c) => c.outcome !== "dropped_low_similarity"
  ).length;
  const selectedDocumentCount = trace.candidates.filter(isSelected).length;

  const sorted = [...trace.candidates].sort(compareCandidates);
  const pageCandidates = sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const verdictBySource = new Map((judgments ?? []).map((j) => [j.source_id, j.verdict]));
  const judgedDocumentCount = trace.candidates.filter(isJudged).length;
  const irrelevantDocumentCount = [...verdictBySource.values()].filter(
    (v) => v === "IRRELEVANT"
  ).length;

  return (
    <Stack gap={2}>
      <Stack direction="row" flexWrap="wrap" gap={2}>
        <ResultsPaper>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Results
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={3}>
            <div>
              <Typography variant="h6" fontFamily="monospace">
                {selectedDocumentCount} / {documentCount}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                documents selected
              </Typography>
            </div>
            {judgments !== undefined && (
              <div>
                <Typography variant="h6" fontFamily="monospace">
                  {irrelevantDocumentCount} / {judgedDocumentCount}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  judged irrelevant by the LLM
                </Typography>
              </div>
            )}
            <div>
              <Typography variant="h6" fontFamily="monospace">
                {matchingDocumentCount} / {documentCount}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                documents above the threshold
              </Typography>
            </div>
          </Stack>
        </ResultsPaper>

        <ConfigPaper>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Search configuration
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={3}>
            <StatTile configKey="semantic_threshold" value={`≥${trace.semantic_threshold}`} />
            <StatTile configKey="semantic_limit" value={trace.semantic_limit} />
            <StatTile configKey="quality_threshold" value={`≥${trace.quality_threshold}`} />
            <StatTile configKey="quality_limit" value={trace.quality_limit} />
          </Stack>
        </ConfigPaper>
      </Stack>

      <TablePaper>
        <TableIntro>
          <Typography variant="caption" color="text.secondary">
            {documentCount} candidate document(s), judged first — click ▾ to expand paragraphs
          </Typography>
        </TableIntro>
        <DenseTable size="small">
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              <TableCell align="right">
                <Tooltip title={columnTooltips.get("document_score")!} placement="top" arrow>
                  <span style={{ cursor: "help" }}>Score</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title={columnTooltips.get("cross_design_quality")!} placement="top" arrow>
                  <span style={{ cursor: "help" }}>Quality</span>
                </Tooltip>
              </TableCell>
              <TableCell>Judge</TableCell>
              <TableCell align="right">Search gate</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageCandidates.map((candidate) => (
              <CandidateRow
                key={candidate.source_id}
                candidate={candidate}
                judgeVerdict={verdictBySource.get(candidate.source_id)}
              />
            ))}
          </TableBody>
        </DenseTable>
        <PaginationFooter
          count={documentCount}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={CANDIDATE_PAGE_SIZE_OPTIONS}
          onPageChange={(_, next) => setPage(next)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
        />
      </TablePaper>
    </Stack>
  );
}
